import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as RightsActions from '../actions/rights.actions';
import { NavController, ToastController } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { AppState } from '../app.reducer';
import { ErrorService } from 'src/app/services/error.service';
import { IRightModel, RightModel } from 'src/app/core/models/rights.model';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class RightsEffects {


    constructor(
        private actions$: Actions,
        private http: HttpClient,
        private store: Store<AppState>,
        private errorService: ErrorService,
    ) { }

    createRights$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RightsActions.createRights),
            switchMap(({ payload }) => {
                const { id, rights } = payload;
                this.store.dispatch(
                    RightsActions.rightsLoading({ payload: { loading: true } })
                );
                return this.http.post<any>(`${environment.apiUrl}/rights/${id}/createRights`, { rights })
                    .pipe(
                        switchMap((response) => {
                            return of(
                                RightsActions.rightsCreated({ rights: response }),
                            );
                        }),
                        catchError((error) => {
                            return of(
                                RightsActions.rightsFail({
                                    payload: { error: this.errorService.mapError(error) },
                                }),
                            );
                        }),
                        finalize(() => {
                            this.store.dispatch(RightsActions.rightsLoading({ payload: { loading: false } }));
                        })
                    );
            })
        )
    );

    getRightsByPropId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RightsActions.getRightsByPropId),
            switchMap((action) => {
                this.store.dispatch(RightsActions.rightsLoading({ payload: { loading: true } }));
                const { propId, page, pageSize } = action.payload;
                return this.http
                    .get<any>(`${environment.apiUrl}/rights/${propId}/rights`, {
                        params: {
                            page: page.toString(),
                            pageSize: pageSize.toString()
                        }
                    })
                    .pipe(
                        map((x) => x.data),
                        switchMap((responseData) => {
                            const payload = {
                                rights: responseData.rows,
                                count: responseData.count,
                                docsCount: responseData.docsCount
                            };
                            return of(
                                RightsActions.getRightsByPropIdSuccess({ payload }),
                                RightsActions.rightsLoading({ payload: { loading: false } })
                            );
                        }),
                        catchError((error) => {
                            return of(
                                RightsActions.rightsFail({
                                    payload: { error: this.errorService.mapError(error) }
                                }),
                                RightsActions.rightsLoading({ payload: { loading: false } })
                            );
                        })
                    );
            })
        )
    );

    updateRights$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RightsActions.updateRight),
            switchMap(({ payload }) => {
                const { id, rights } = payload;
                this.store.dispatch(
                    RightsActions.rightsLoading({ payload: { loading: true } })
                );
                return this.http.post<any>(`${environment.apiUrl}/rights/${id}/rights`, { rights })
                    .pipe(
                        switchMap((response) => {
                            return of(RightsActions.rightsUpdated({ rights: response }));
                        }),
                        catchError((error) => {
                            return of(
                                RightsActions.rightsFail({
                                    payload: { error: this.errorService.mapError(error) },
                                })
                            );
                        }),
                        finalize(() => {
                            this.store.dispatch(RightsActions.rightsLoading({ payload: { loading: false } }));
                        })
                    );
            })
        )
    );

    deleteRight$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RightsActions.deleteRight),
            switchMap(({ payload }) => {
                const { id } = payload;
                this.store.dispatch(
                    RightsActions.rightsLoading({ payload: { loading: true } })
                );
                return this.http.delete<any>(`${environment.apiUrl}/rights/${id}`).pipe(
                    tap(response => {
                    }),
                    map(() => RightsActions.rightDeleted({ payload: { id } })),
                    catchError((error) => {
                        return of(
                            RightsActions.rightsFail({
                                payload: { error: this.errorService.mapError(error) },
                            })
                        );
                    }),
                    finalize(() => {
                        this.store.dispatch(RightsActions.rightsLoading({ payload: { loading: false } }));
                    })
                );
            })
        )
    );

    createExistingDoc$ = createEffect(() =>
        this.actions$.pipe(
            ofType(RightsActions.createExistingDoc),
            switchMap(({ payload }) => {
                const { id, docs, rightId } = payload;
                this.store.dispatch(
                    RightsActions.rightsLoading({ payload: { loading: true } })
                );
                return this.http.post<any>(`${environment.apiUrl}/rights/${id}/createExistingDoc`, { docs, rightId })
                    .pipe(
                        switchMap((response) => {
                            return of(RightsActions.existingDocCreated({ rights: response }));
                        }),
                        catchError((error) => {
                            return of(
                                RightsActions.rightsFail({
                                    payload: { error: this.errorService.mapError(error) },
                                })
                            );
                        }),
                        finalize(() => {
                            this.store.dispatch(RightsActions.rightsLoading({ payload: { loading: false } }));
                        })
                    );
            })
        )
    );
    getRightsData$ = createEffect(() =>
        this.actions$.pipe(
          ofType(RightsActions.getRightsData),
          switchMap(() => {
            return this.http.get<{ right_subcat_descr: string; count: string }[]>(
              `${environment.apiUrl}/rights-type`
            ).pipe(
              map((response) => {
                if (response && Array.isArray(response)) {
                  const formattedData = response.map((item) => ({
                    ...item,
                    count: parseInt(item.count, 10), // Ensure count is a number
                  }));
                  return RightsActions.getRightsDataSuccess({ payload: formattedData });
                } else {
                  throw new Error('Invalid response structure');
                }
              }),
              catchError((error) => {
                console.error('Error fetching rights data:', error);
                return of(
                  RightsActions.rightsFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              })
            );
          })
        )
      );


      getDocType$=createEffect(() =>
        this.actions$.pipe(
          ofType(RightsActions.getDocType),
          switchMap(() => {
            return this.http.get<{ doc_type_descr: string; count: string}[]>(
              `${environment.apiUrl}/doc-type`
            ).pipe(
              map((response) => {
                if (response && Array.isArray(response)) {
                  const formattedData = response.map((item) => ({
                    ...item,
                    count: parseInt(item.count, 10),// Ensure count is a number
                  }));
                  return RightsActions.getDocTypeSuccess({ payload:  formattedData});
                } else {
                  throw new Error('Invalid response structure');
                }
              }),
              catchError((error) => {
                console.error('Error fetching rights data:', error);
                return of(
                  RightsActions.rightsFail({
                    payload: { error: this.errorService.mapError(error) },
                  })
                );
              })
            );
          })
        )
      );
}

